import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ApiError,
  downloadPrintSpool,
  recordSpoolOutcome,
  retryPrintSpool,
  sendPrintSpool,
  type PrintSpool,
  type SpoolOutcome,
} from "@/api/client";
import { saveBlob } from "@/components/download";
import { formatDate } from "@/components/format";
import { ErrorBanner, StatusBadge, ToolbarButton } from "@/components/ui";

const BUILT_STATUSES: readonly PrintSpool["status"][] = ["ready", "sent", "failed"];
// The server recovers queued or sending only once stale (409 before); it owns that clock.
const RETRYABLE_STATUSES: readonly PrintSpool["status"][] = ["failed", "queued", "sending"];
const NOTE_LIMIT = 1000;
// Exact message from internal/certificates/print_service.go's failRevokedSinceBuild.
const REVOKED_SINCE_BUILT_ERROR = "a certificate was revoked since the spool was built; rebuild it";
// SendNotConfirmedMessage from internal/certificates/print_service.go; the worker appends the cause.
const SEND_NOT_CONFIRMED_ERROR = "send not confirmed; check the printer before resending";

type Confirming = "send" | "retry" | SpoolOutcome | null;

export const SPOOL_COLUMNS = 10;

export function SpoolRow({
  spool,
  filename,
  canPrint,
  canSend,
  sendRun,
  rebuilt,
  onRetried,
  onSent,
  onChanged,
}: {
  spool: PrintSpool;
  filename: string;
  canPrint: boolean;
  /** The run's profile has an IPP address the server may contact. */
  canSend: boolean;
  sendRun: boolean;
  rebuilt: boolean;
  onRetried: () => void;
  onSent: () => void;
  onChanged: () => Promise<void>;
}) {
  const label = `spool ${spool.seq}`;
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [note, setNote] = useState("");
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);

  // A 409 means the spool moved on (another tab, the worker). A 5xx or lost connection can
  // follow a claim the server already committed (print_spools.go's failUnqueuedSend flips the
  // spool to failed, or leaves it sending) so the row must not keep showing its old status.
  const refetchIfCommitted = async (error: unknown) => {
    if (!(error instanceof ApiError) || error.status === 409 || error.status >= 500) await onChanged();
  };
  const download = useMutation({
    mutationFn: async () => saveBlob(await downloadPrintSpool(spool.id), filename),
  });
  const send = useMutation({
    mutationFn: () => sendPrintSpool(spool.id),
    onSuccess: async () => {
      setConfirming(null);
      onSent();
      await onChanged();
    },
    onError: refetchIfCommitted,
  });
  const retry = useMutation({
    mutationFn: () => retryPrintSpool(spool.id),
    onSuccess: async (next) => {
      setConfirming(null);
      // A stale sending spool comes back failed, not rebuilt: it was never re-rendered.
      if (next.status === "queued") onRetried();
      await onChanged();
    },
    onError: refetchIfCommitted,
  });
  const outcome = useMutation({
    mutationFn: ({ outcome, note }: { outcome: SpoolOutcome; note?: string }) => recordSpoolOutcome(spool.id, outcome, note),
    onSuccess: async () => {
      setConfirming(null);
      setNote("");
      await onChanged();
    },
    onError: refetchIfCommitted,
  });
  const busy = download.isPending || send.isPending || retry.isPending || outcome.isPending;
  const error = download.error ?? send.error ?? retry.error ?? outcome.error;
  const start = () => {
    download.reset();
    send.reset();
    retry.reset();
    outcome.reset();
  };
  const ask = (next: Confirming) => {
    start();
    setNote("");
    setConfirming(next);
  };
  // A same-tick second click fires before isPending re-renders; this ref blocks it synchronously.
  const sending = useRef(false);
  const triggerSend = () => {
    if (sending.current) return;
    sending.current = true;
    start();
    send.mutate(undefined, { onSettled: () => (sending.current = false) });
  };

  const built = spool.file_available && BUILT_STATUSES.includes(spool.status);
  // Matches print_service.go's failRevokedSinceBuild: already failed for a reason Send can't fix.
  const revokedSinceBuilt = spool.last_error === REVOKED_SINCE_BUILT_ERROR;
  // The rebuilt spool is ready, and Send on ready doesn't ask, so the possible double print is asked about here.
  const mayHavePrinted = spool.status === "failed" && (spool.last_error?.startsWith(SEND_NOT_CONFIRMED_ERROR) ?? false);
  const triggerRetry = () => {
    start();
    retry.mutate();
  };
  const revoked =
    download.error instanceof ApiError && download.error.status === 409 && download.error.message.includes("revoked");
  // Shown only once the rebuild is actually ready to send; a queued or re-failed rebuild isn't.
  const showRebuilt = rebuilt && sendRun && canSend && spool.status === "ready" && spool.file_available;

  return (
    <>
      <tr className="align-top">
        <td className="px-2 py-2 font-medium">{spool.seq}</td>
        <td className="px-2 py-2 font-mono">{`${spool.first_certificate_no} – ${spool.last_certificate_no}`}</td>
        <td className="px-2 py-2">{spool.certificate_count}</td>
        <td className="px-2 py-2">
          <StatusBadge status={spool.status} />
          {showRebuilt ? <p className="mt-1 text-amber-700">Rebuilt; press Send to print.</p> : null}
        </td>
        <td className="px-2 py-2">{spool.attempts}</td>
        <td className="px-2 py-2">{spool.ipp_job_id ?? "-"}</td>
        <td className="px-2 py-2 text-red-700">{spool.last_error ?? "-"}</td>
        <td className="px-2 py-2">{formatDate(spool.sent_at ?? undefined)}</td>
        <td className="px-2 py-2">{formatDate(spool.printed_at ?? undefined)}</td>
        <td className="px-2 py-2">
          {canPrint ? (
            <div className="flex flex-wrap gap-1">
              {spool.file_available ? (
                <ToolbarButton
                  aria-label={`Download ${label}`}
                  disabled={busy}
                  onClick={() => {
                    start();
                    download.mutate();
                  }}
                >
                  {download.isPending ? "Downloading..." : "Download"}
                </ToolbarButton>
              ) : null}
              {canSend && built && !revokedSinceBuilt ? (
                <ToolbarButton tone="primary"
                  aria-label={`Send ${label}`}
                  disabled={busy || confirming !== null}
                  onClick={() => {
                    // Sending a sent or failed spool may print it twice, so that asks first.
                    if (spool.status === "ready") triggerSend();
                    else ask("send");
                  }}
                >
                  {send.isPending ? "Sending..." : "Send"}
                </ToolbarButton>
              ) : null}
              {RETRYABLE_STATUSES.includes(spool.status) ? (
                <ToolbarButton
                  aria-label={`Retry ${label}`}
                  disabled={busy || confirming !== null}
                  onClick={() => (mayHavePrinted ? ask("retry") : triggerRetry())}
                >
                  {retry.isPending ? "Retrying..." : "Retry"}
                </ToolbarButton>
              ) : null}
              {built ? (
                <>
                  <ToolbarButton aria-label={`Mark ${label} printed`} disabled={busy || confirming !== null} onClick={() => ask("printed")}>
                    Printed
                  </ToolbarButton>
                  <ToolbarButton
                    aria-label={`Mark ${label} failed`}
                    tone="danger"
                    disabled={busy || confirming !== null}
                    onClick={() => ask("failed")}
                  >
                    Failed
                  </ToolbarButton>
                </>
              ) : null}
            </div>
          ) : null}
        </td>
      </tr>
      {confirming || error ? (
        <tr>
          <td colSpan={SPOOL_COLUMNS} className="space-y-2 px-2 py-2">
            {confirming === "send" ? (
              <div role="group" aria-label={`Confirm send ${label} again`} className="flex flex-wrap items-center gap-2">
                <p className="min-w-64 flex-1 text-zinc-700">
                  Send {label} again? It may already have printed; check the printer before sending it again.
                </p>
                <ToolbarButton ref={confirmButton} disabled={busy} onClick={triggerSend}>
                  {send.isPending ? "Sending..." : `Send ${label} again`}
                </ToolbarButton>
                <ToolbarButton disabled={busy} onClick={() => setConfirming(null)}>
                  Cancel
                </ToolbarButton>
              </div>
            ) : null}
            {confirming === "retry" ? (
              <div role="group" aria-label={`Confirm retry ${label}`} className="flex flex-wrap items-center gap-2">
                <p className="min-w-64 flex-1 text-zinc-700">
                  Rebuild {label}? It may already have printed; check the printer before rebuilding it to send again.
                </p>
                <ToolbarButton ref={confirmButton} disabled={busy} onClick={triggerRetry}>
                  {retry.isPending ? "Rebuilding..." : `Rebuild ${label}`}
                </ToolbarButton>
                <ToolbarButton disabled={busy} onClick={() => setConfirming(null)}>
                  Cancel
                </ToolbarButton>
              </div>
            ) : null}
            {confirming === "printed" || confirming === "failed" ? (
              <div role="group" aria-label={`Confirm mark ${label} ${confirming}`} className="space-y-2">
                <p className="text-zinc-700">
                  {confirming === "printed"
                    ? `Mark ${label} printed? This is final: its certificates are recorded as delivered, except any revoked since it was built (recorded as failed).`
                    : `Mark ${label} failed? Its certificates are recorded as not printed; Retry rebuilds it.`}
                </p>
                <label className="block">
                  <span className="font-medium text-zinc-700">Note (optional)</span>
                  <textarea
                    rows={2}
                    maxLength={NOTE_LIMIT}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <ToolbarButton
                    ref={confirmButton}
                    tone={confirming === "failed" ? "danger" : "default"}
                    disabled={busy}
                    onClick={() => outcome.mutate({ outcome: confirming, note: note.trim() || undefined })}
                  >
                    {outcome.isPending ? "Saving..." : `Mark ${label} ${confirming}`}
                  </ToolbarButton>
                  <ToolbarButton disabled={busy} onClick={() => setConfirming(null)}>
                    Cancel
                  </ToolbarButton>
                </div>
              </div>
            ) : null}
            <ErrorBanner error={error} />
            {revoked ? (
              <p className="text-zinc-600">
                {spool.status === "printed"
                  ? "The file can no longer be downloaded: a certificate in it was revoked after the spool was built."
                  : spool.status === "failed"
                    ? "Retry to rebuild it without the revoked certificate."
                    : "Mark it failed, then Retry to rebuild it without the revoked certificate."}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
